import clsx from "clsx";
import { useState } from "react";

import { ProgressHeader } from "@/pages/quiz/base/components/progressHeader/progressHeader";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { LogoIcon } from "@/assets/images";
import { AnimatedContainer } from "@/components/common/animatedContainer";
import { QuizIDs } from "@/pages/quiz/context/common";
import { useQuizContext } from "@/pages/quiz/context";
import GeneralError from "@/components/GeneralError";
import { QuestionPrerenderContainer } from "@/pages/quiz/components/questionPrerenderContainer";
import { getQuestionBlock } from "@/pages/quiz/base/common/blockMapper";
import { QuestionPreview } from "@/pages/quiz/base/components/questionPreview";
import { FooterAb } from "@/pages/quiz/base/components/footerAB";
import QuestionContentDisplay from "@/pages/quiz/base/components/progressHeader/components/QuestionContentDisplay";
import { ProgressHeaderV1 } from "@/pages/quiz/base/components/progressHeaderV1/ProgressHeaderV1";
import { HelpButton } from "@/components/common/helpButton";
import { HelpModal } from "@/components/HelpModal";
import { clsxm } from "@/utils/clsxm";
import { Header } from "@/pages/quiz/base/components/header";
import { useIsShowHelpButtonT22 } from "@/pages/quiz/base/hooks/useIsShowHelpButtonT22";
import { HelpButtonT22 } from "@/components/common/HelpButtonT22";

interface IProps {
  isHideProgressBackButton?: boolean;
  quizId?: string;
}

const QuizV1: React.FC<IProps> = ({ quizId, isHideProgressBackButton }) => {
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const {
    questions,
    selectedQuestion,
    answers,
    uploadUserId,
    isPrerenderEnabled,
    selectedQuestionIdx,
    onChangeQuestionValue,
    onAnswer,
    onBack,
    loadQuizBlocksErrorMessage,
  } = useQuizContext();

  const { Component, config } = getQuestionBlock(selectedQuestion);

  const { shouldShowHelpButtonT22, isDefaultHelpButtonHidden } =
    useIsShowHelpButtonT22(quizId ?? "");

  const isQuizWithNewDesign = quizId === QuizIDs["12000"];
  const isRenderDefaultHelpButton =
    isQuizWithNewDesign && !isDefaultHelpButtonHidden;

  useScrollToTop([selectedQuestion?.id]);

  const questionHasAnswer =
    selectedQuestion?.id && !!answers?.[selectedQuestion?.id]?.value;

    const isSumsubAttachmentBlock = selectedQuestion?.name === "sumsub-verification";
    
  return (
    <div
      className={clsx(
        "flex flex-col h-full min-h-[100svh] justify-between",
        isQuizWithNewDesign && "bg-violet-100"
      )}
    >
      {isQuizWithNewDesign ? (
        <Header />
      ) : (
        <header>
          <div className="h-40 sm:h-60 lg:h-80 bg-light-pink flex justify-center items-start">
            <LogoIcon />
          </div>
        </header>
      )}

      {selectedQuestion && (
        <QuestionPreview
          PreviewComponent={config?.PreviewComponent}
          disablePreview={
            questionHasAnswer ||
            !!selectedQuestion?.answers?.length ||
            !isQuizWithNewDesign
          }
        >
          <div>
            {isQuizWithNewDesign && (
              <ProgressHeaderV1 hideProgressBar={!!config?.hideProgressBar} />
            )}
            <div
              className={clsxm(
                "bg-white w-full sm:w-618 p-[16px_16px_24px] sm:p-[30px_75px_60px] sm:drop-shadow-[0_3px_4px_rgba(0,0,0,0.25)] mx-auto mb-20 mt-0",
                isQuizWithNewDesign
                  ? "mt-10 max-w-[calc(100%-32px)] rounded-xl"
                  : "mt-[-6rem] sm:mt-[-10rem] old-quiz",
                isRenderDefaultHelpButton && "relative",
                shouldShowHelpButtonT22 && "mb-0",
                isSumsubAttachmentBlock && "sm:!w-[518px] sm:p-12"
              )}
            >
              <>
                {!isQuizWithNewDesign && (
                  <ProgressHeader
                    hideProgressBar={!!config?.hideProgressBar}
                    isHideBackButton={
                      isHideProgressBackButton || !!config?.isHideBackButton?.(quizId ?? "")
                    }
                  />
                )}
                {isQuizWithNewDesign &&
                  !!selectedQuestion &&
                  !!selectedQuestion.attributes.question && (
                    <QuestionContentDisplay
                      question={selectedQuestion.attributes.question}
                      prompt={selectedQuestion.attributes.prompt}
                      customHTML={selectedQuestion.attributes.customHTML}
                      descriptionBlock={
                        selectedQuestion.attributes.descriptionBlock
                      }
                    />
                  )}

                <AnimatedContainer
                  questionId={selectedQuestion.id}
                  disableAnimation={
                    !config?.enableAnimation ||
                    !!selectedQuestion?.answers?.length ||
                    !isQuizWithNewDesign
                  }
                >
                  <Component
                    questionValue={
                      (answers[selectedQuestion.id]?.value as any) ?? ""
                    }
                    setQuestionAnswer={async (
                      v: any,
                      useServerValidation = false,
                      disableApi = false
                    ) =>
                      onAnswer(
                        selectedQuestion.id,
                        v,
                        useServerValidation,
                        disableApi
                      )
                    }
                    setQuestionValue={(v: any, isAnswered = false) =>
                      onChangeQuestionValue({
                        questionId: selectedQuestion.id,
                        answer: v,
                        isAnswered,
                      })
                    }
                    onBack={onBack}
                    question={selectedQuestion}
                    uploadUserId={uploadUserId}
                    quizId={quizId}
                  />
                </AnimatedContainer>
              </>

              {isRenderDefaultHelpButton && (
                <div
                  className={clsx(
                    "block sm:hidden",
                    "absolute",
                    "left-1/2 -translate-x-1/2 -bottom-16",
                    "min-[1200px]:left-full min-[1200px]:translate-x-0 min-[1200px]:ml-6 min-[1200px]:-bottom-8"
                  )}
                >
                  <HelpButton
                    label="How does it work?"
                    onClick={() => setIsHelpModalOpen(true)}
                  />
                </div>
              )}
            </div>

            {shouldShowHelpButtonT22 && (
              <div className="w-full flex justify-center py-6 sm:py-8">
                <HelpButtonT22
                  label="How does it work?"
                  onClick={() => setIsHelpModalOpen(true)}
                />
              </div>
            )}
          </div>
        </QuestionPreview>
      )}
      {isRenderDefaultHelpButton && (
        <div
          className={clsx(
            "hidden sm:block",
            "fixed bottom-8 right-10 w-fit z-50"
          )}
        >
          <HelpButton
            label="How does it work?"
            onClick={() => setIsHelpModalOpen(true)}
          />
        </div>
      )}
      {!selectedQuestion && <GeneralError title={loadQuizBlocksErrorMessage} />}

      <FooterAb />
      <HelpModal
        open={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
      {/* used for prerendering components */}
      <QuestionPrerenderContainer
        questions={questions}
        selectedQuestionIdx={selectedQuestionIdx}
        isPrerenderEnabled={isPrerenderEnabled}
        getQuestionBlock={getQuestionBlock}
      />
    </div>
  );
};

export { QuizV1 };
